import { Button, Code, CopyButton, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import type { DomainRecord } from "../website/domain-queries";

/**
 * The DNS records a church adds at the company that runs its domain, shared by Website → Domain and Settings →
 * Sign-in addresses (ADR 0067 S4), so both show them the same way.
 */

/** One value the church copies into its registrar, with a button that says what it copied. */
export function Copyable({ value, what }: { readonly value: string; readonly what: string }) {
  return (
    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
      <Code style={{ overflowWrap: "anywhere", minWidth: 0 }}>{value}</Code>
      <CopyButton value={value}>
        {({ copied, copy }) => (
          <Button
            variant="default"
            size="compact-xs"
            onClick={copy}
            style={{ flexShrink: 0 }}
            aria-label={copied ? `Copied the ${what}` : `Copy the ${what}`}
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}

/**
 * The records to add, each as three labelled values. Not a table: on a phone a table scrolls sideways and hides the
 * value, which is the one thing the person came to copy. Here the three sit side by side when there is room and
 * stack when there is not.
 */
export function RecordList({
  records,
  caption,
}: {
  readonly records: readonly DomainRecord[];
  readonly caption: string;
}) {
  return (
    <Stack gap="xs" role="group" aria-label={caption}>
      {records.map((r) => (
        <SimpleGrid
          key={`${r.type}:${r.name}`}
          component="dl"
          cols={{ base: 1, sm: 3 }}
          spacing="sm"
          verticalSpacing="xs"
          m={0}
          p="sm"
          style={{
            border: "1px solid var(--how-border-subtle)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <div>
            <Text component="dt" size="xs" c="dimmed">
              Type
            </Text>
            <Text component="dd" size="sm" m={0}>
              {r.type}
            </Text>
          </div>
          <div style={{ minWidth: 0 }}>
            <Text component="dt" size="xs" c="dimmed">
              Name (some companies call it Host)
            </Text>
            <Text component="dd" size="sm" m={0}>
              <Copyable value={r.name} what={`${r.type} record's name`} />
            </Text>
          </div>
          <div style={{ minWidth: 0 }}>
            <Text component="dt" size="xs" c="dimmed">
              Value (or Target, Points to)
            </Text>
            <Text component="dd" size="sm" m={0}>
              <Copyable value={r.value} what={`${r.type} record's value`} />
            </Text>
          </div>
        </SimpleGrid>
      ))}
    </Stack>
  );
}
